import React from "react"
import { Box, Paper, Grid, Divider } from "@mui/material"
import { makeStyles } from "makeStyles"
import Rating from "@mui/material/Rating"

import Button from "components/Buttons/Button"
import Link from "components/i18n/Link"
import Price from "components/Price"
import LazyLoadedImage from "components/LazyLoadedImage"
import ListViewItemAttributes from "./ListViewItemAttributes"
import ProductHitStructuredData from "@secureo/common/components/StructuredData/ProductHitStructuredData"

import useProductHitImpressionTracking from "@secureo/common/hooks/tracking/useProductHitImpressionTracking"

import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"
import { trackProductHitClick } from "@secureo/common/utils/tracking/productHit"

import { AlgoliaProductHit } from "@secureo/common/typings/Algolia"

const useStyles = makeStyles()({
	attributes: {
		visibility: "hidden",
		marginTop: "-90px",
		opacity: 0.4,
	},
	button: {
		background: "#E3E5E6",
	},
	title: {
		color: "black",
		cursor: "pointer",
	},
	product: {
		boxShadow: "none",
		border: "1px solid whitesmoke",
		borderRadius: 10,
		transition: "0.3s",
		position: "relative",
		marginBottom: 10,
		"&:hover": {
			boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			border: "1px solid #4691CE",
			"& $button": {
				background: "#4691CE",
				color: "white",
				boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			},
			"& $title": {
				color: "#4691CE",
			},
		},
	},
	badge: {
		position: "absolute",
		top: 15,
		left: 15,
		fontSize: 10,
		textTransform: "uppercase",
		border: "1px solid #4691CE",
		color: "#4691CE",
		borderRadius: 5,
		paddingLeft: 5,
		paddingRight: 5,
		"&:hover": {
			color: "white",
			background: "#4691CE",
			cursor: "pointer",
		},
	},
})

const ListViewItem = ({ hit, locale, position, listName = "", categoryName }: Props) => {
	const { classes } = useStyles()
	const trackProductHitImpression = useProductHitImpressionTracking(
		hit,
		categoryName,
		listName || categoryName,
		position,
	)

	const {
		name,
		grossPriceCents,
		image,
		deliveryTimeWorkdays,
		slug,
		manufacturer,
		rating = 0,
		productBadge,
	} = hit

	const onClick = () => {
		trackProductHitClick(
			name,
			grossPriceCents,
			manufacturer,
			categoryName,
			listName || categoryName,
			position,
		)
	}

	return (
		<Link href={`/p/${slug}`} onClick={onClick}>
			<div style={{ position: "relative" }}>
				<Paper className={classes.product}>
					<Grid container spacing={0}>
						{/* Product Image */}
						<Grid item xs={12} md={3}>
							<Box py={2} style={{ textAlign: "center" }}>
								<LazyLoadedImage
									alt={name}
									title={name}
									src={image}
									style={{
										height: 130,
										maxWidth: 200,
										objectFit: "contain",
										width: "100%",
										margin: "auto",
									}}
									onIntersection={trackProductHitImpression}
								/>
							</Box>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box p={1}>
								{/* Rating */}
								<Box mt={1}>
									<Rating
										name="read-only"
										value={rating}
										precision={0.5}
										readOnly
									/>
								</Box>
								{/* Product Name */}
								<Box mt={1} className={classes.title} style={{ maxWidth: 480 }}>
									<strong>{name}</strong>
								</Box>
								{/* shortDescription */}
								<Box my={1} style={{ maxWidth: 480 }}>
									<Divider />
								</Box>
								<ListViewItemAttributes hit={hit} />
							</Box>
						</Grid>
						{/* Price & CTA Button */}
						<Grid
							item
							xs={12}
							md={3}
							style={{
								background: "#E7EFF6",
								borderTopRightRadius: 10,
								borderBottomRightRadius: 10,
							}}
						>
							<Box pt={4} px={2}>
								{/* Price */}
								<Box ml={1} mt={1}>
									<strong style={{ fontSize: 22, marginRight: 5 }}>
										<Price priceInEuroCents={grossPriceCents} />
									</strong>
									<span style={{ color: "grey", fontSize: 12 }}>inkl. MwSt.</span>
									<br />
									<Box style={{ fontSize: 12, color: "#505e5f" }}>
										Lieferzeit:{" "}
										{getDeliveryTimeWorkDaysString(deliveryTimeWorkdays)}
									</Box>
								</Box>
								{/* To Product | Add to Cart Button */}
								<Box my={2}>
									<Button color="primary" aria-label="to-product" size="small">
										Zum Produkt
									</Button>
								</Box>
							</Box>
						</Grid>
					</Grid>

					{productBadge && (
						<div className={classes.badge}>
							<strong>{productBadge}</strong>
						</div>
					)}
				</Paper>
				<ProductHitStructuredData product={hit} locale={locale} />
			</div>
		</Link>
	)
}

interface Props {
	hit: AlgoliaProductHit
	locale: string
	position: number
	categoryName: string
	listName?: string
}

export default ListViewItem
